import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import Text from '../ui/Text'
import { QUESTION_OPTIONS_PREVIEW } from './constants'

const ROW_CLASS = 'tw:flex tw:items-center tw:gap-2 tw:text-sm'
const TEXT_COL_CLASS = 'tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:gap-0.5 tw:justify-center'
const PROGRESS_TRACK_CLASS = 'tw:w-full tw:h-2 tw:bg-gray-200 tw:rounded-full tw:overflow-hidden'
const TOGGLE_CLASS = 'tw:bg-transparent tw:border-0 tw:text-sm tw:text-gray-800 '
  + 'tw:cursor-pointer tw:flex tw:items-center tw:gap-1'
const TOGGLE_LABEL_CLASS = 'tw:underline'
const FADE_OVERLAY_CLASS = 'tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-40 '
  + 'tw:bg-gradient-to-b tw:from-transparent tw:via-white tw:to-white '
  + 'tw:flex tw:items-end tw:justify-center tw:pb-6 tw:pointer-events-none'

const QuestionSelectionContent = ({ question }) => {
  const [showAll, setShowAll] = useState(false)
  const options = question.options || []
  const total = question.responses_count || 0
  const canExpand = options.length > QUESTION_OPTIONS_PREVIEW
  const handleToggle = useCallback(() => setShowAll(previous => !previous), [])

  const items = (
    <ul className="tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-4">
      {options.map(option => {
        const isZero = (option.count || 0) === 0
        const labelClass = isZero ? 'tw:text-gray-600' : 'tw:text-gray-800'
        const valueClass = isZero ? 'tw:text-gray-600' : 'tw:text-gray-800'
        return (
          <li key={option.id} className="tw:flex tw:flex-col tw:gap-2">
            <div className={ROW_CLASS}>
              <div className={TEXT_COL_CLASS}>
                <Text
                  className={`tw:break-all tw:font-normal ${labelClass}`}
                  lines={2}
                >
                  {option.label}
                </Text>
                <span className="tw:text-sm tw:text-gray-600">
                  {`${option.count} / ${total} réponses`}
                </span>
              </div>
              <span className={`tw:font-semibold ${valueClass} tw:whitespace-nowrap`}>
                {`${option.percentage} %`}
              </span>
            </div>
            <div className={PROGRESS_TRACK_CLASS}>
              {/* Inline style required: bar fill width is dynamic per option. */}
              <div
                className={'tw:h-full tw:bg-ds-primary-blue tw:rounded-full '
                  + 'tw:border-l tw:border-solid tw:border-white'}
                style={{ width: `${Math.max(0, Math.min(100, option.percentage))}%` }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )

  if (!canExpand || showAll)
    return <div className="tw:flex tw:flex-col tw:gap-4">{items}</div>

  return (
    <div className="tw:relative tw:max-h-[320px] tw:overflow-hidden">
      {items}
      <div className={FADE_OVERLAY_CLASS}>
        <button
          className={`${TOGGLE_CLASS} tw:pointer-events-auto`}
          type="button"
          onClick={handleToggle}
        >
          <span className={TOGGLE_LABEL_CLASS}>Afficher toutes les données</span>
          <i aria-hidden="true" className="fas fa-chevron-down" />
        </button>
      </div>
    </div>
  )
}

QuestionSelectionContent.propTypes = {
  question: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      count: PropTypes.number,
      id: PropTypes.number,
      label: PropTypes.string,
      percentage: PropTypes.number
    }))
  }).isRequired
}

export default QuestionSelectionContent
